/**
 * guidance.service.ts
 *
 * First-week and weeks 2–4 guidance balloon evaluation.
 *
 * Rules:
 *   - One guidance balloon per day maximum
 *   - Celebration takes priority — guidance only fires if no celebration today
 *   - Active window: Days 0–28 after onboarding (deactivates automatically)
 *   - Guidance is display-only — nothing is stored in Firestore
 *   - Daily gate uses local device date (same as celebration system)
 */

import { getDailyNutrition } from './nutrition.service';
import { getUserProfile } from './userProfile.service';
import { getTodayDate, getDateDaysAgo } from '../utils/date';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GuidanceResult {
  message: string;
  subtext?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const GUIDANCE_GATE_KEY = 'coretrak.guidance.shownDate';
const ACTIVE_WINDOW_DAYS = 28;

// ─── Local date helper ────────────────────────────────────────────────────────

function getLocalDateString(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ─── Daily gate ───────────────────────────────────────────────────────────────

export function hasShownGuidanceToday(): boolean {
  try {
    return localStorage.getItem(GUIDANCE_GATE_KEY) === getLocalDateString();
  } catch {
    return false;
  }
}

export function markGuidanceShownToday(): void {
  try {
    localStorage.setItem(GUIDANCE_GATE_KEY, getLocalDateString());
  } catch { /* best effort */ }
}

// ─── Days since signup helper ─────────────────────────────────────────────────
// Uses local calendar dates — a user who signed up at 11pm and opens next morning
// sees day 1, not day 0.

function daysBetweenLocalDates(fromTs: number, toDate: string): number {
  const from = new Date(fromTs);
  const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
  const [fy, fm, fd] = fromStr.split('-').map(Number);
  const [ty, tm, td] = toDate.split('-').map(Number);
  const fromMs = Date.UTC(fy, fm - 1, fd);
  const toMs   = Date.UTC(ty, tm - 1, td);
  return Math.floor((toMs - fromMs) / 86400000);
}

// ─── Logging count helpers ────────────────────────────────────────────────────

async function countLoggedDaysInRange(userId: string, daysBack: number): Promise<number> {
  const dates = Array.from({ length: daysBack }, (_, i) => getDateDaysAgo(i + 1));
  const results = await Promise.allSettled(
    dates.map(d => getDailyNutrition(userId, d).catch(() => null))
  );
  return results.filter(
    r => r.status === 'fulfilled' && r.value && (r.value.entries?.length ?? 0) > 0
  ).length;
}

async function loggedYesterday(userId: string): Promise<boolean> {
  const d = await getDailyNutrition(userId, getDateDaysAgo(1)).catch(() => null);
  return (d?.entries?.length ?? 0) > 0;
}

async function loggedToday(userId: string): Promise<boolean> {
  const d = await getDailyNutrition(userId, getTodayDate()).catch(() => null);
  return (d?.entries?.length ?? 0) > 0;
}

async function daysSinceLastLog(userId: string): Promise<number> {
  for (let i = 0; i <= 30; i++) {
    const d = await getDailyNutrition(userId, getDateDaysAgo(i)).catch(() => null);
    if ((d?.entries?.length ?? 0) > 0) return i;
  }
  return 31;
}

// ─── Week 1 guidance (Days 0–7) ───────────────────────────────────────────────

async function evaluateWeek1(
  userId: string,
  day: number,
): Promise<GuidanceResult | null> {
  const hasLoggedToday = await loggedToday(userId);
  const hasLoggedYesterday = await loggedYesterday(userId);

  // Universal restart — overrides day-specific message if user is returning after 48h gap
  const sinceLastLog = await daysSinceLastLog(userId);
  if (sinceLastLog >= 2 && hasLoggedToday) {
    return { message: 'Welcome back — pick up where you left off.' };
  }


  switch (day) {
    case 0: {
      if (hasLoggedToday) return null;
      // Check if 6+ hours since signup (fallback message)
      // We approximate via createdAt — if day is still 0 and no meal logged, show prompt
      const profile = await getUserProfile(userId).catch(() => null);
      const hoursSinceSignup = profile?.createdAt
        ? (Date.now() - profile.createdAt) / 3600000
        : 0;
      if (hoursSinceSignup >= 6) {
        return { message: 'Logging takes less than 10 seconds.' };
      }
      return {
        message: 'You\'re set up.',
        subtext: 'Start by logging your first meal.',
      };
    }

    case 1:
      return hasLoggedYesterday
        ? { message: 'Great start — keep logging today.' }
        : { message: 'Let\'s begin today — one meal is enough.' };

    case 2: {
      const logged2 = await countLoggedDaysInRange(userId, 2);
      return logged2 >= 2
        ? { message: 'Two days done — consistency builds quickly.' }
        : { message: 'Back on track today — that\'s what matters.' };
    }

    case 3: {
      const logged3 = await countLoggedDaysInRange(userId, 3);
      return logged3 >= 2
        ? { message: 'You\'re building a habit now.' }
        : { message: 'Logging once today keeps momentum going.' };
    }

    case 4:
      return { message: 'Small steps each day make progress sustainable.' };

    case 5: {
      const logged5 = await countLoggedDaysInRange(userId, 5);
      return logged5 >= 3
        ? { message: 'You\'re forming a routine.' }
        : { message: 'Start again today — progress resumes immediately.' };
    }

    case 6:
      return { message: 'Consistency matters more than perfection.' };

    case 7: {
      const logged7 = await countLoggedDaysInRange(userId, 7);
      return logged7 >= 4
        ? { message: 'Week one complete — keep going.' }
        : { message: 'Week one done — today is a fresh start.' };
    }

    default:
      return null;
  }
}

// ─── Week 2 guidance (Days 8–14) ─────────────────────────────────────────────

async function evaluateWeek2(
  userId: string,
  day: number,
): Promise<GuidanceResult | null> {
  const loggedRecent = await countLoggedDaysInRange(userId, 4);
  const isConsistent = loggedRecent >= 3;

  switch (day) {
    case 8:
      return { message: 'You\'re into week two — keep the routine simple.' };

    case 9:
      return isConsistent
        ? { message: 'Consistency now matters more than speed.' }
        : { message: 'A single entry today keeps the routine alive.' };

    case 10:
      return { message: 'Progress comes from showing up regularly.' };

    case 11:
      return isConsistent
        ? { message: 'You\'re building reliability — keep going.' }
        : { message: 'Restarting today is always the right move.' };

    case 12:
      return { message: 'Small daily actions create long-term change.' };

    case 13:
      return isConsistent
        ? { message: 'You\'re becoming consistent now.' }
        : { message: 'One step today is enough.' };

    case 14:
      return { message: 'Two weeks in — consistency is working.' };

    default:
      return null;
  }
}

// ─── Week 3 guidance (Days 15–21) ────────────────────────────────────────────

async function evaluateWeek3(
  userId: string,
  day: number,
): Promise<GuidanceResult | null> {
  const loggedRecent = await countLoggedDaysInRange(userId, 4);
  const isConsistent = loggedRecent >= 3;

  switch (day) {
    case 15:
      return { message: 'You\'ve built momentum — protect it.' };

    case 16:
      return isConsistent
        ? { message: 'You\'re in control of the routine now.' }
        : { message: 'Returning today keeps progress moving.' };

    case 17:
      return { message: 'Progress is happening, even if it\'s gradual.' };

    case 18:
      return { message: 'Consistency beats intensity every time.' };

    case 19:
      return isConsistent
        ? { message: 'Your routine is becoming reliable.' }
        : { message: 'Start again today — nothing is lost.' };

    case 20:
      return { message: 'You\'re doing the right things — keep them simple.' };

    case 21:
      return { message: 'Three weeks in — habits are forming.' };

    default:
      return null;
  }
}

// ─── Week 4 guidance (Days 22–28) ────────────────────────────────────────────

async function evaluateWeek4(
  userId: string,
  day: number,
): Promise<GuidanceResult | null> {
  const loggedRecent = await countLoggedDaysInRange(userId, 4);
  const isConsistent = loggedRecent >= 3;

  switch (day) {
    case 22:
      return { message: 'You\'re building a sustainable routine.' };

    case 23:
      return isConsistent
        ? { message: 'Consistency is now part of your day.' }
        : { message: 'Today is always a fresh start.' };

    case 24:
      return { message: 'Keep the routine steady — that\'s what works.' };

    case 25:
      return { message: 'You\'re staying consistent — that\'s the real progress.' };

    case 26:
      return isConsistent
        ? { message: 'You\'re maintaining momentum.' }
        : { message: 'Restarting today keeps things moving.' };

    case 27:
      return { message: 'You\'re proving consistency is possible.' };

    case 28:
      return { message: 'Four weeks in — your routine is established.' };

    default:
      return null;
  }
}

// ─── Universal behaviour-triggered guidance ───────────────────────────────────
// These fire at any point within the 28-day window based on behaviour.

async function evaluateUniversal(userId: string): Promise<GuidanceResult | null> {
  const sinceLastLog = await daysSinceLastLog(userId);
  const hasLoggedToday = await loggedToday(userId);

  // Returning after 48h+ absence (brief spec: 48h = trigger)
  if (sinceLastLog >= 2 && hasLoggedToday) {
    return { message: 'Welcome back — pick up where you left off.' };
  }

  // Low logging — 48h without entries, not yet returned today
  if (sinceLastLog >= 2 && !hasLoggedToday) {
    return { message: 'Logging once today gets things moving again.' };
  }

  // Strong consistency — 7 of last 7
  const logged7 = await countLoggedDaysInRange(userId, 7);
  if (logged7 === 7) {
    return { message: 'You\'re staying consistent day after day.' };
  }

  // Good consistency — 5 of last 7
  if (logged7 >= 5) {
    return { message: 'You\'re keeping the routine steady.' };
  }

  return null;
}

// ─── Main evaluation entry point ──────────────────────────────────────────────

/**
 * Evaluate which guidance message (if any) the user should see today.
 *
 * Returns null if:
 *   - Outside the 28-day active window
 *   - Guidance already shown today
 *   - No message qualifies
 *
 * Does NOT store anything — guidance is ephemeral.
 */
export async function evaluateGuidance(userId: string): Promise<GuidanceResult | null> {
  if (!userId) return null;
  if (hasShownGuidanceToday()) return null;

  const profile = await getUserProfile(userId).catch(() => null);
  if (profile?.notifications_enabled === false) return null;
  if (!profile?.createdAt) return null;

  const today = getLocalDateString();
  const day = daysBetweenLocalDates(profile.createdAt, today);

  // Outside active window
  if (day > ACTIVE_WINDOW_DAYS) return null;

  let result: GuidanceResult | null = null;

  try {
    // Universal behaviour triggers take priority within any week
    result = await evaluateUniversal(userId);

    // Day-specific message if no universal trigger
    if (!result) {
      if (day <= 7)       result = await evaluateWeek1(userId, day);
      else if (day <= 14) result = await evaluateWeek2(userId, day);
      else if (day <= 21) result = await evaluateWeek3(userId, day);
      else                result = await evaluateWeek4(userId, day);
    }
  } catch (err) {
    console.error('[guidance] Evaluation failed:', err);
    return null;
  }

  if (result) markGuidanceShownToday();
  return result;
}
